import Vue from 'vue';
import {
    fetchSavedViews,
    fetchSavedView,
    createSavedView,
    updateSavedView,
    deleteSavedView,
    generateSavedViewSummary,
} from './saved-views.api';
import { formatView, hasAccountTypesFilter, unmapFilters } from './saved-views.utils';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { formatAISummaryForEditor } from '@/voc/helpers/common.helper';
import cloneDeep from 'lodash/cloneDeep';
import { hasSetupExplore } from '@/voc/helpers/permissions.helper';
import { fetchAccountTypes } from '@/accounts/helpers/account-types.helper';

const promiseStore = new Map();

const savedViews = {
    namespaced: true,
    state: {
        error: {},
        errorOne: {},
        loaded: false,
        fetching: false,
        fetchingOne: null,
        map: {
            feedback: [],
        },
        counts: {
            feedback: {},
        },
        savedView: {},
        emptyFiltersGetter: {
            feedback: 'vocItems/filters/feedbackEmptyFilters',
            feedbackExplore: 'vocItems/filters/feedbackExploreEmptyFilters',
        },
        filtersGetter: {
            feedback: 'vocItems/filters/allFilters',
        },
        filtersSetter: {
            feedback: 'vocItems/filters/updateFilters',
        },
        emptyTableGetter: {
            feedback: 'vocItems/table/feedbackEmptyTableConfig',
        },
        promptGetter: {
            feedback: 'vocItems/filters/prompt',
        },
        promptSetter: {
            feedback: 'vocItems/filters/updatePrompt',
        },
        tableGetter: {
            feedback: 'vocItems/table/tableConfig',
        },
        tableSetter: {
            feedback: 'vocItems/table/update',
        },
    },
    mutations: {
        setLoaded (state, { loaded }) {
            state.loaded = loaded;
        },
        setFetching (state, { isFetching }) {
            state.fetching = isFetching;
        },
        setFetchingOne (state, { id }) {
            state.fetchingOne = id;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setErrorOne (state, { id, error }) {
            state.error = { id, error };
        },
        setSavedViews (state, { savedViews, type }) {
            Vue.set(state.map, type, savedViews);
        },
        setSavedView (state, { savedView, version }) {
            state.savedView = savedView;
            if (savedView.id && !version) {
                if (!state.map[savedView.type]) Vue.set(state.map, savedView.type, []);
                const viewIndex = state.map[savedView.type]?.findIndex(
                    (storedSavedView) => storedSavedView.id === savedView.id,
                );
                if (viewIndex === -1 || viewIndex === undefined) {
                    state.map[savedView.type].push(savedView);
                } else {
                    Vue.set(state.map[savedView.type], viewIndex, savedView);
                }
            }
        },
        setSavedViewsCounts (state, { countData, type }) {
            Vue.set(state.counts, type, countData);
        },
        updateSavedView (state, { savedView }) {
            if (!state.map[savedView.type]) Vue.set(state.map, savedView.type, []);
            const viewIndex = state.map[savedView.type].findIndex(
                (storedSavedView) => storedSavedView.id === savedView.id,
            );

            if (viewIndex === -1) {
                state.map[savedView.type].push(savedView);
            } else {
                Vue.set(state.map[savedView.type], viewIndex, savedView);
            }
        },
        setSavedViewProperty (state, { propertyKey, propertyValue }) {
            Vue.set(state.savedView, propertyKey, propertyValue);
        },
        resetSavedView (state) {
            state.savedView = {};
        },
    },
    actions: {
        async loadAll ({ commit, state, rootGetters, dispatch, rootState }, { reFetch = false, type, version } = {}) {
            if (!isEmpty(state.map[type]) && !reFetch && state.loaded) {
                return;
            }
            try {
                commit('setFetching', { fetching: true });
                const fetchedSavedViews = await fetchSavedViews(type, version);
                const addEmptyFilters = !hasSetupExplore();
                const hasAccountTypesFilterCheck = fetchedSavedViews.some((savedView) =>
                    hasAccountTypesFilter(savedView),
                );
                if (hasAccountTypesFilterCheck) {
                    await fetchAccountTypes({ dispatch });
                }

                const savedViews = state.emptyFiltersGetter[type]
                    ? fetchedSavedViews.map((savedView) => {
                        return formatView(savedView, {
                            emptyFilters: rootGetters[state.emptyFiltersGetter[type]],
                            emptyTable: rootGetters[state.emptyTableGetter[type]],
                            addEmptyFilters,
                            store: {
                                getters: rootGetters,
                                state: rootState,
                            },
                        });
                    })
                    : fetchedSavedViews;

                commit('setSavedViews', { savedViews, type });
                commit('setLoaded', { loaded: true });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async loadOne ({ commit, state, rootGetters, dispatch, rootState }, { reFetch = false, id, version } = {}) {
            if (state.savedView?.id === id && !reFetch) return state.savedView;
            if (promiseStore.get(id)) return promiseStore.get(id);

            const addEmptyFilters = !hasSetupExplore();

            commit('setFetchingOne', { id });
            commit('setErrorOne', { id: null, error: null });
            promiseStore.set(
                id,
                fetchSavedView(id, version)
                    .then(async (fetchedView) => {
                        if (hasAccountTypesFilter(fetchedView)) {
                            await fetchAccountTypes({ dispatch });
                        }

                        return fetchedView;
                    })
                    .then((fetchedView) => {
                        const savedView = state.emptyFiltersGetter[fetchedView.type]
                            ? formatView(fetchedView, {
                                emptyFilters: rootGetters[state.emptyFiltersGetter[fetchedView.type]],
                                emptyTable: rootGetters[state.emptyTableGetter[fetchedView.type]],
                                addEmptyFilters,
                                store: {
                                    getters: rootGetters,
                                    state: rootState,
                                },
                            })
                            : fetchedView;
                        commit('setSavedView', { savedView, version });

                        state.map[savedView.type]?.forEach((view) => {
                            if (view.id !== savedView.id) {
                                delete view.applied;
                            }
                        });

                        return savedView;
                    })
                    .catch((error) => {
                        commit('setErrorOne', { id, error });
                        throw error;
                    })
                    .finally(() => {
                        commit('setFetchingOne', { id: null });
                        promiseStore.delete(id);
                    }),
            );

            return promiseStore.get(id);
        },
        async createSavedView ({ state, commit, rootGetters, dispatch, rootState }, { savedView: savedViewToSave }) {
            try {
                const newSavedView = {
                    ...savedViewToSave,
                    filters: unmapFilters(savedViewToSave.filters),
                };
                const addEmptyFilters = !hasSetupExplore();
                const createdSavedView = await createSavedView(newSavedView);
                if (hasAccountTypesFilter(createdSavedView)) {
                    await fetchAccountTypes({ dispatch });
                }

                const savedView = state.emptyFiltersGetter[createdSavedView.type]
                    ? formatView(createdSavedView, {
                        emptyFilters: rootGetters[state.emptyFiltersGetter[createdSavedView.type]],
                        emptyTable: rootGetters[state.emptyTableGetter[createdSavedView.type]],
                        addEmptyFilters,
                        store: {
                            getters: rootGetters,
                            state: rootState,
                        },
                    })
                    : createdSavedView;
                savedView.isNew = true;

                commit('setSavedViews', {
                    savedViews: [savedView, ...state.map[savedView?.type]],
                    type: savedView?.type,
                });

                return savedView;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        generateEmptySavedView ({ state, commit, rootGetters, rootState }, { name, type, filters = {} }) {
            const addEmptyFilters = !hasSetupExplore();
            const emptySavedView = state.emptyFiltersGetter[type]
                ? formatView(
                    { name, type, filters },
                    {
                        emptyFilters: rootGetters[state.emptyFiltersGetter[type]],
                        emptyTable: rootGetters[state.emptyTableGetter[type]],
                        addEmptyFilters,
                        store: {
                            getters: rootGetters,
                            state: rootState,
                        },
                    },
                )
                : { name, type };
            commit('setSavedView', { savedView: emptySavedView });
        },
        async update (
            { commit, dispatch, state, rootGetters, rootState },
            { savedView: savedViewToSave = null, persist = false, property = null },
        ) {
            if (savedViewToSave) {
                commit('setSavedView', { savedView: savedViewToSave });
            }

            let { savedView } = state;

            if (persist) {
                let updatedSavedView = { id: state.savedView.id };
                if (property === null) {
                    updatedSavedView = {
                        ...state.savedView,
                        filters: unmapFilters(state.savedView.filters),
                    };
                } else if (property === 'filters') {
                    updatedSavedView[property] = unmapFilters(state.savedView.filters);
                } else {
                    updatedSavedView[property] = state.savedView[property];
                }

                const returnedSavedView = await updateSavedView(updatedSavedView);
                const addEmptyFilters = !hasSetupExplore();
                if (hasAccountTypesFilter(returnedSavedView)) {
                    await fetchAccountTypes({ dispatch });
                }

                savedView = state.emptyFiltersGetter[returnedSavedView.type]
                    ? formatView(returnedSavedView, {
                        emptyFilters: rootGetters[state.emptyFiltersGetter[returnedSavedView.type]],
                        emptyTable: rootGetters[state.emptyTableGetter[returnedSavedView.type]],
                        addEmptyFilters,
                        store: {
                            getters: rootGetters,
                            state: rootState,
                        },
                    })
                    : returnedSavedView;

                if (savedView.id === state.savedView.id) {
                    if (state.savedView.isNew) {
                        savedView.isNew = true;
                    }
                    if (state.savedView.applied) {
                        savedView.applied = true;
                    }
                    if (state.savedView.isGeneratingSummary) {
                        savedView.isGeneratingSummary = true;
                    }
                }
                if (!hasSetupExplore()) {
                    commit('setSavedView', { savedView });
                    dispatch('applySavedView', { filterParams: { persist: false, persistGlobal: false } });
                }
            }

            if (savedView?.type) {
                const updatedSavedViews = state.map[savedView?.type]?.map((view) => {
                    if (view.id === savedView?.id) {
                        return savedView;
                    }

                    return view;
                });
                commit('setSavedViews', { savedViews: updatedSavedViews, type: savedView?.type });
            }

            return savedView;
        },
        applySavedView (
            { dispatch, commit, state },
            { filterParams = {}, tableParams = {}, resetFilters = false, version } = {},
        ) {
            if (state.filtersSetter[state.savedView.type] && state.savedView.filters) {
                dispatch(
                    state.filtersSetter[state.savedView.type],
                    { filters: state.savedView.filters, ...(filterParams && { ...filterParams }), reset: resetFilters },
                    { root: true },
                );
            }

            if (state.promptSetter[state.savedView.type]) {
                dispatch(state.promptSetter[state.savedView.type], { prompt: state.savedView.prompt }, { root: true });
            }
            const updatedColumnState = {};
            if (state.tableSetter[state.savedView.type]) {
                let columns;
                if (state.savedView.columns) {
                    columns = state.savedView.columns;
                    if (!state.savedView.columns.includes('source') && state.savedView.type === 'feedback') {
                        columns = ['source', ...columns];
                    }
                    if (!state.savedView.columns.includes('checkbox')) {
                        columns = ['checkbox', ...columns];
                    }
                    updatedColumnState.columns = columns;
                } else {
                    columns = null;
                }

                const tableUpdates = {
                    type: state.savedView.type,
                    sort: state.savedView.sort ? state.savedView.sort : null,
                    columns,
                    columnWidths: state.savedView.columnWidths ? state.savedView.columnWidths : null,
                    ...(tableParams && { ...tableParams }),
                };
                dispatch(state.tableSetter[state.savedView.type], tableUpdates, { root: true });
            }

            const updatedSavedView = {
                ...state.savedView,
                ...updatedColumnState,
                applied: true,
            };

            commit('setSavedView', { savedView: updatedSavedView, version });
        },
        async updateCurrentSavedView ({ state, dispatch, commit }, { property, value, persist = true }) {
            if (property === 'name' || property === 'description') {
                delete state.savedView.isNew;
                if (value === state.savedView[property]) return;
            }
            commit('setSavedViewProperty', { propertyKey: property, propertyValue: value });
            const returnedSavedView = await dispatch('update', { persist, property });

            return returnedSavedView;
        },
        async removeSavedView ({ state, commit, getters }, { savedViewId }) {
            try {
                const savedView = cloneDeep(getters.savedViewById(savedViewId));
                await deleteSavedView(savedViewId);
                commit('setSavedViews', {
                    savedViews: state.map[savedView.type].filter((view) => view.id !== savedViewId),
                    type: savedView.type,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async generateSavedViewSummary ({ commit, getters, dispatch }, { feedbackIds, format, savedViewId }) {
            try {
                const savedView = getters.savedViewById(savedViewId);
                commit('setSavedView', { savedView });

                await dispatch('updateCurrentSavedView', {
                    property: 'isGeneratingSummary',
                    value: true,
                    persist: false,
                });

                const summary = await generateSavedViewSummary(feedbackIds, format);
                const formattedSummary = formatAISummaryForEditor(summary, format);

                await dispatch('updateCurrentSavedView', {
                    property: 'summary',
                    value: {
                        count: feedbackIds.length,
                        userEdited: false,
                        summary: formattedSummary,
                    },
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                await dispatch('updateCurrentSavedView', {
                    property: 'isGeneratingSummary',
                    value: false,
                    persist: false,
                });
            }
        },
        updateItemCounts ({ commit }, { type, countData }) {
            commit('setSavedViewsCounts', { countData, type });
        },
        updateItemCount ({ commit, state }, { type, id, count }) {
            commit('setSavedViewsCounts', {
                countData: {
                    ...state.counts?.[type],
                    [id]: count,
                },
                type,
            });
        },
    },
    getters: {
        savedViewsByType: (state) => (type = 'feedback') =>
            state.map[type]
                ?.filter((view) => {
                    // saved view must have been created after August 30th 2023 GMT
                    // due the filters changing and old saved views having invalid filters
                    return view.createdAt > 1693402102312 || view.ownedByUser.username === '--system-user--';
                })
                .map((view) => {
                    return {
                        ...view,
                        count: state.counts[type]?.[view.id],
                    };
                }),
        savedViewById: (state) => (id) => {
            let savedView = null;
            Object.keys(state.map).forEach((type) => {
                if (!savedView) {
                    savedView = state.map[type].find((view) => view.id === id);
                    if (savedView) {
                        savedView = {
                            ...savedView,
                            count: state.counts[type]?.[id],
                        };
                    }
                }
            });

            return savedView;
        },
        savedView: (state) => {
            if (state.savedView) {
                return {
                    ...state.savedView,
                    count: state.counts[state.savedView.type]?.[state.savedView.id],
                };
            }

            return state.savedView;
        },
        isViewDirty: (state, _getters, _rootState, rootGetters) => {
            let isDirty = false;
            if (state.savedView.filters && state.filtersGetter[state.savedView.type]) {
                const currentFilters = [
                    ...Object.keys(state.savedView.filters),
                    ...Object.keys(rootGetters[state.filtersGetter[state.savedView.type]]).filter(
                        (key) => !Object.keys(state.savedView.filters).includes(key),
                    ),
                ];
                currentFilters.forEach((filter) => {
                    const savedFilter =
                        state.savedView.filters[filter] !== undefined
                            ? JSON.parse(JSON.stringify(state.savedView.filters[filter]))
                            : null;
                    const currentFilter =
                        rootGetters[state.filtersGetter[state.savedView.type]][filter] !== undefined
                            ? JSON.parse(JSON.stringify(rootGetters[state.filtersGetter[state.savedView.type]][filter]))
                            : null;
                    if (!isDirty && !isEqual(savedFilter, currentFilter)) {
                        isDirty = true;
                    }
                });
            }
            if (
                !isDirty &&
                state.promptGetter[state.savedView.type] &&
                (state.savedView.prompt || '') !== (rootGetters[state.promptGetter[state.savedView.type]] || '')
            ) {
                isDirty = true;
            }
            if (!isDirty && state.tableGetter[state.savedView.type]) {
                const savedViewTableConfig = {
                    ...(state.savedView.sort && { sort: state.savedView.sort }),
                    ...(state.savedView.columns && { columns: state.savedView.columns }),
                    ...(state.savedView.columnWidths && { columnWidths: state.savedView.columnWidths }),
                };
                if (!isDirty && !isEqual(savedViewTableConfig, rootGetters[state.tableGetter[state.savedView.type]])) {
                    isDirty = true;
                }
            }

            return isDirty;
        },
        allFeedbackId: (_state, getters) => getters.allFeedbackView?.id,
        allFeedbackView: (state) => {
            let allFeedbackView = state.map.feedback.find(
                (view) => view.name === 'All Feedback' && view.createdByUser.username === '--system-user--',
            );

            if (allFeedbackView) {
                allFeedbackView = {
                    ...allFeedbackView,
                    count: state.counts[allFeedbackView.type]?.[allFeedbackView.id],
                };
            }

            return allFeedbackView;
        },
        isSeededView: (_state, getters) => (id) => {
            return getters.savedViewById(id)?.createdByUser.username === '--system-user--';
        },
        isGeneratingAnySummary: (state) => (type = 'feedback') => {
            return state.map[type]?.some((view) => view.isGeneratingSummary);
        },
    },
};

export default savedViews;
